<template>
    <header :class="{ 'header--tall': isTall }">
        <section
            class="partner-banner nav-bar"
            v-if="isShowPartnerBanner && $config.partnerSiteBanner"
            >
            <div class="nav-bar__inner">
                <a
                    target="_blank"
                    :href="$config.partnerSiteBanner.siteUrl"
                    >
                    <img
                        v-if="partnerLogoMobile"
                        :alt="partnerLogoMobile.alt"
                        :class="{ 'fx-hidden--medium fx-hidden--large fx-hidden--full': partnerLogoDesktop }"
                        :src="partnerLogoMobile.url"
                        :style="partnerLogoMobile.overrideStyles"
                    >
                    <img
                        v-if="partnerLogoDesktop"
                        :alt="partnerLogoDesktop.alt"
                        :class="{ 'fx-hidden--small': partnerLogoMobile }"
                        :src="partnerLogoDesktop.url"
                        :style="partnerLogoDesktop.overrideStyles"
                    >
                </a>
                <p class="partner-banner__copy">
                    {{ $config.partnerSiteBanner.message }}
                    <router-link
                        class="partner-banner__legal"
                        v-if="$config.partnerLegal"
                        :to="{ query: updateQueryParams($route.query, { modal: 'partner-legal' }) }">
                            Learn more
                    </router-link>
                </p>
            </div>
        </section>
        <section class="nav-bar">
            <div
                class="nav-bar__inner"
                :class="{ 'nav-bar__inner--fluid': isFluid }">
                <div
                    class="nav-bar__logo-container"
                    :class="{ 'nav-bar__logo-container--tab': hasLogoTab, 'fx-hidden--small fx-hidden--medium': isFluid }"
                >
                    <router-link
                        class="nav-bar__logo"
                        @click.native="onClickLogo"
                        :to="$store.state.selectedLocation
                            ? fixPath(`/?lat=${$store.state.selectedLocation.latitude}&lon=${$store.state.selectedLocation.longitude}&location_id=${$store.state.selectedLocation.id}`)
                            : { name: 'home' }"
                    >
                            <img
                                data-e2e="site-header.logo"
                                v-if="logoMobile"
                                :alt="logoMobile.alt"
                                :class="{ 'fx-hidden--medium fx-hidden--large fx-hidden--full': logoDesktop }"
                                :src="logoMobile.url"
                                :style="logoMobile.overrideStyles"
                            >
                            <img
                                data-e2e="site-header.logo"
                                v-if="logoDesktop"
                                :alt="logoDesktop.alt"
                                :class="{ 'fx-hidden--small': logoMobile }"
                                :src="logoDesktop.url"
                                :style="logoDesktop.overrideStyles"
                            >
                    </router-link>
                </div>

                <button
                    v-if="event"
                    class="fx-hidden--large fx-hidden--full nav-bar__back"
                    type="button"
                    :class="{ 'fx-hidden--small fx-hidden--medium': !isFluid }"
                    @click="onClickBackButton"><i class="icon-chevron-lg-left nav-bar__back-icon"></i></button>

                <div v-if="event && !isExclusiveEvent"
                    class="event-information"
                    data-e2e="event-information"
                >
                    <div class="event-title-container">
                        <h1 class="event-title">{{ event.name }}</h1>
                    </div>
                    <div class="event-details">
                        <span v-if="event.is_date_tbd">Date TBD</span>
                        <span v-else-if="event.is_time_tbd">{{ event.local_date | date('ddd, MMM D') }}, Time TBD</span>
                        <span v-else-if="showYear">{{ event.local_date | date('ddd, MMM D') }}, {{ event.local_date | date('YYYY') }}</span>
                        <span v-else>{{ event.local_date | date('ddd, MMM D') }}</span>
                        <span v-if="!event.is_time_tbd"> at {{ event.local_date | date('h:mma') }}</span> -
                        {{ event.venue.name }},
                        {{ event.venue.city_name }}
                        {{ event.venue.subdivision }}
                    </div>
                </div>

                <nav
                    class="primary-nav fx-hidden--small fx-hidden--medium fx-hidden--large"
                    v-else-if="isShowMenu">
                    <ul class="primary-nav__list">
                        <li
                            class="primary-nav__item"
                            v-for="category in highlights"
                            :class="{ 'primary-nav__item--active': activeCategory === category }"
                            data-e2e="site-header.category"
                            :key="category.name">
                            <button
                                class="primary-nav__item-button"
                                type="button"
                                @click="onClickNavigationCategory"
                                @keypress.enter.prevent="onCategoryButtonPressEnter(category)"
                                @mouseout="onPrimaryNavMouseOver"
                                @mouseover="setExpandNavTimeout(category)">{{ category.name }}</button>
                        </li>
                    </ul>
                </nav>

                <div
                    class="nav-bar__right"
                    :class="{ 'nav-bar__right--fluid': isFluid }">
                    <section
                        class="inline-search"
                        :class="{ 'fx-hidden--small fx-hidden--medium': isFluid }"
                        v-if="isShowSearch && !hasBigSearch">
                        <form
                            data-e2e="home.search-form-inline"
                            @submit.prevent="onSearch"
                            @keyup.down="onIncrementHighlightedSuggestionIndex"
                            @keyup.up="onDecrementHighlightedSuggestionIndex">
                            <span class="icon icon-magnify"></span>
                            <input
                                autocomplete="true"
                                class="inline-search__input"
                                ref="searchInput"
                                type="search"
                                placeholder="Search"
                                v-on-click-outside="hideSearchSuggestions"
                                @focus="showSearchSuggestions"
                                @input="onSearchInput">
                        </form>
                        <inline-search-suggestions
                            v-if="isShowSuggestions"
                            :highlighted-suggestion="highlightedSuggestion"
                            :make-entity-route="makeEntityRoute"
                            :on-suggestion-click="onSuggestionClick"
                            :suggestions="suggestions">
                            <div
                                slot="event"
                                slot-scope="{ event }">
                                <h4 class="inline-search__suggestion-primary">{{ event.name }}</h4>
                                <h5 class="inline-search__suggestion-secondary">
                                    <template v-if="event.is_date_tbd">Date TBD</template>
                                    <template v-else-if="event.is_time_tbd">{{ event.local_date | date('ddd, MMM D') }}, Time TBD</template>
                                    <template v-else>{{ event.local_date | date('ddd, MMM D, h:mma') }}</template>
                                    &ndash; {{ event.venue }}<template v-if="event.city">, {{ event.city }}</template><template v-if="event.region">, {{ event.region }}</template>
                                </h5>
                            </div>
                            <div
                                slot="performer"
                                slot-scope="{ performer }">
                                <h4 class="inline-search__suggestion-primary">{{ performer.name }}</h4>
                                <h5 class="inline-search__suggestion-secondary">{{ `${performer.count} ${performer.count === 1 ? 'event' : 'events'}` }}</h5>
                            </div>
                            <div
                                slot="venue"
                                slot-scope="{ venue }">
                                <h4 class="inline-search__suggestion-primary">{{ venue.name }}</h4>
                                <h5 class="inline-search__suggestion-secondary">{{ `${venue.count} ${venue.count === 1 ? 'event' : 'events'}` }}</h5>
                            </div>
                            <div
                                slot="city"
                                slot-scope="{ city }">
                                <h4 class="inline-search__suggestion-primary">{{ city.name }}</h4>
                                <h5 class="inline-search__suggestion-secondary">{{ `${city.count} ${city.count === 1 ? 'event' : 'events'}` }}</h5>
                            </div>
                        </inline-search-suggestions>
                    </section>

                    <div
                        class="account"
                        v-if="userProfile">
                        <button
                            class="account__welcome account__welcome--button"
                            type="button"
                            :class="{ 'has-credit-card': creditCardType && lastFour }"
                            @click="toggleAccountMenu">
                            <span class="account__welcome-inner fx-hidden--small fx-hidden--medium account__welcome-inner--expandable">
                                <slot name="welcome">Welcome, {{ userProfile.first_name }}</slot>
                                <span
                                    v-if="(hasMyAccountButton || hasMyTicketsButton || hasLogout)"
                                    class="account__welcome-icon"
                                    :class="{ 'icon-chevron-lg-up': isAccountMenuOpen, 'icon-chevron-lg-down': !isAccountMenuOpen }"></span>
                            </span>
                            <span class="account__welcome-inner fx-hidden--large fx-hidden--full account__welcome-inner--expandable">
<!--                                <slot name="welcome-mobile">-->
                                    <span class="icon-profile"></span>
<!--                                </slot>-->
                                <span
                                    class="account__welcome-icon"
                                    :class="{
                                        'icon-chevron-lg-up': isAccountMenuOpen,
                                        'icon-chevron-lg-down': !isAccountMenuOpen,
                                        'no-credit-card--mobile': !creditCardType && !lastFour
                                    }">
                                </span>
                            </span>
                        </button>
                        <ul
                            class="account__menu"
                            v-if="isAccountMenuOpen"
                            v-on-click-outside="closeAccountMenu">
                            <li class="account__menu-item fx-hidden--large fx-hidden--full">
                                <span class="account__menu-item--welcome">Welcome, {{ userProfile.first_name }}</span>
                                <span
                                    class="account_menu-item--user-info"
                                    v-if="$store.state.loyalty">
                                    <span class="user-info__credit-card" v-if="creditCardType && lastFour">
                                        <img
                                            v-if="creditCardType"
                                            class="account__menu-item--credit-cards__icon"
                                            :alt="creditCardType.displayName"
                                            :src="$store.state.imagesCdn + creditCardType.iconSlug">
                                        <span class="user-info__middot">&middot;&middot;&middot;&middot;</span>
                                        <span class="user-info__last-four">{{ lastFour }}</span>
                                        <span class="user-info__pipe">|</span>
                                    </span>
                                    <span class="user-info__loyalty-balance">{{ formatPoints($store.state.loyalty.balance) }}</span>
                                </span>
                                <button
                                    class="account__menu-close"
                                    type="button"
                                    @click="closeAccountMenu">
                                    <span class="icon-close account__menu-close-icon"></span>
                                </button>
                            </li>
                            <li
                                class="account__menu-item"
                                v-if="hasMyAccountButton">
                                <router-link
                                    class="account__menu-action"
                                    :to="{ name: 'account' }"
                                    @click.native="closeAccountMenu">My Account
                                        <span class="icon-chevron-lg-right fx-hidden--full fx-hidden--large"></span>
                                    </router-link>
                            </li>
                            <li
                                class="account__menu-item"
                                v-if="hasMyTicketsButton">
                                <router-link
                                    class="account__menu-action"
                                    :to="{ name: 'purchases-upcoming' }"
                                    @click.native="closeAccountMenu">My Tickets
                                        <span class="icon-chevron-lg-right fx-hidden--full fx-hidden--large"></span>
                                    </router-link>
                            </li>
                            <li
                                class="account__menu-item"
                                v-if="hasLogout">
                                <span
                                    class="account__menu-action"
                                    @click="logout">{{ ($config.auth || {}).logoutOverrideText || 'Logout' }}</span>
                            </li>
                        </ul>
                    </div>
                    <router-link
                        class="sign-in-button"
                        replace
                        v-else-if="hasLoginButton"
                        @click.native="onClickLogin"
                        :to="{ name: $route.name, query: updateQueryParams($route.query, { modal: 'login' }) }">
                        <span class="icon--large icon-signin"></span>
                        <span class="fx-hidden--small fx-hidden--medium">Sign in</span>
                    </router-link>
                </div>
            </div>

            <section
                class="subnav"
                v-if="(subcategories.length || results.length) && !this.isWindowSmall"
                v-on-click-outside="onClickOutsideSubnav"
                @mouseout="setCollapseNavTimeout"
                @mouseover="clearCollapseNavTimeout">
                <div class="subnav__inner">
                    <ul
                        class="subnav__nav-left"
                        ref="subnavLeft"
                        v-if="subcategories.length > 0">
                        <li
                            class="subnav__nav-left-item"
                            v-for="subcategory in subcategories"
                            :class="{ 'subnav__nav-left-item--active': activeSubcategory === subcategory }"
                            :key="subcategory.name"
                            @keyup.right="addFocusToFirstRightNavItem"
                            @mouseover="setActiveSubcategory(subcategory)">
                            <router-link
                                class="subnav__nav-left-text"
                                v-if="subcategory.slug"
                                :to="$store.state.selectedLocation
                                    ? fixPath(`${subcategory.slug}?lat=${$store.state.selectedLocation.latitude}&lon=${$store.state.selectedLocation.longitude}&location_id=${$store.state.selectedLocation.id}`)
                                    : fixPath(subcategory.slug)"
                                @click.native="setActiveCategory(null)"
                                @focus.native="setActiveSubcategory(subcategory)">{{ subcategory.name }}</router-link>
                            <span class="subnav__nav-left-text" v-else>{{ subcategory.name }}</span>
                        </li>
                    </ul>
                    <ul
                        class="subnav__nav-right"
                        :class="{ 'subnav__nav-right--exclusive': activeCategory && activeCategory.id === 'exclusive' }"
                        ref="subnavRight">
                        <li
                            class="subnav__nav-right-item"
                            v-for="result in results"
                            :key="result.name"
                            @click="onClickPerformer(result.name)"
                            @keyup.left="addFocusToActiveSubcategory">
                            <router-link
                                class="subnav__nav-right-link"
                                :class="{ 'subnav__nav-see-all': result.slug === '/exclusive-events' }"
                                :to="$store.state.selectedLocation
                                    ? fixPath(`${result.slug}?lat=${$store.state.selectedLocation.latitude}&lon=${$store.state.selectedLocation.longitude}&location_id=${$store.state.selectedLocation.id}`)
                                    : fixPath(result.slug)"
                                @click.native="setActiveCategory(null)">{{ result.name }}</router-link>
                        </li>
                    </ul>
                </div>
            </section>
        </section>

        <section
            class="not-found"
            v-if="isShowPageNotFound">
            <h1>Nothing to see here.</h1>
            <h2>Don’t worry, we’ll get you back on track.<br>Try searching again.</h2>
        </section>

        <section
            class="search"
            v-if="isShowSearch && hasBigSearch">
            <form
                class="search__form"
                @submit.prevent="onSearch"
                @keyup.down="onIncrementHighlightedSuggestionIndex"
                @keyup.up="onDecrementHighlightedSuggestionIndex">
                <div class="search__form-inner">
                    <span class="search-icon icon-magnify"></span>
                    <input
                        autocomplete="true"
                        autofocus
                        class="search__input"
                        ref="searchInput"
                        :placeholder="isWindowSmall ? 'Search for a team, artist or venue' : 'Search your favorite team, artist or venue'"
                        :type="!isWindowSmall ? 'search' : null"
                        @click="onOpenMobileNav"
                        @input="onSearchInput">
                        <span
                            class="icon-close"
                            v-if="isShowSearchClose && isWindowSmall"
                            @click="clearSearchQueryAndCloseMobileNav">
                        </span>
                </div>
            </form>

            <transition name="fade">
                <section
                    class="recents"
                    v-if="$store.state.recentSearches.length > 0">
                    <span>Recent</span>
                    <transition-group
                        class="recents__list"
                        name="fade"
                        tag="ul">
                        <li
                            class="recents__search"
                            v-for="(search, index) in $store.state.recentSearches"
                            :key="makeSearchItemKey(search)">
                            <router-link
                                class="link"
                                :title="search.label"
                                :to="fixPath(search.route)"
                                @click.native="onRecentSearchClick(search)">{{ search.label }}</router-link>
                            <button
                                class="icon-cancel"
                                type="button"
                                @click="removeRecentSearchAt(index)"></button>
                        </li>
                    </transition-group>
                </section>
            </transition>

            <transition name="slide-fade">
                <section v-if="isMobileNavOpen" class="mobile-nav">
                    <h3
                        class="mobile-nav__nav-element mobile-nav__nav-header"
                        data-e2e="site-header.mobile-nav.subcategory"
                        v-if="activeSubcategory"
                        @click="setActiveCategory(highlights[0])"
                    >
                        <span class="icon-chevron-lg-left mobile-nav__icon"></span>
                        <span>{{activeSubcategory.name}}</span>
                    </h3>

                    <h3
                        class="mobile-nav__nav-element mobile-nav__nav-header"
                        data-e2e="site-header.mobile-nav.subcategory"
                        v-else-if="activeCategory"
                        @click="setActiveCategory(null)"
                    >
                        <span class="icon-chevron-lg-left mobile-nav__icon"></span>
                        <span>{{activeCategory.name}}</span>
                    </h3>

                    <ul
                        class="mobile-nav__categories"
                        data-e2e="site-header.mobile-nav.categories"
                        v-if="!activeCategory"
                    >
                        <li
                            class="mobile-nav__nav-element mobile-nav__nav-header--uncollapsed"
                            v-for="(category, index) in highlights"
                            :data-e2e="`site-header.mobile-nav.category-${index}`"
                            :key="category.name"
                            @click="setActiveCategory(category)"
                        >
                            <span>{{category.name}}</span>
                            <span class="icon-chevron-lg-right"></span>
                        </li>
                    </ul>

                    <ul
                        class="mobile-nav__subcategories"
                        data-e2e='site-header.mobile-nav.leagues'
                        v-if="!activeSubcategory"
                    >
                        <li
                            class="mobile-nav__nav-element"
                            v-for="(subcategory, index) in subcategories"
                            :data-e2e='`site-header.mobile-nav.league-${index}`'
                            :key="subcategory.name"
                            @click="setActiveSubcategory(subcategory)">
                            <span>{{subcategory.name}}</span>
                            <span class="icon-chevron-lg-right"></span>
                        </li>
                    </ul>

                    <ul
                        class="mobile-nav__performers"
                        data-e2e='site-header.mobile-nav.performers'
                    >
                        <li v-if="(activeSubcategory || {}).slug">
                            <router-link
                                class="mobile-nav__nav-element mobile-nav__router"
                                :to="fixPath(activeSubcategory.slug)"
                            >
                                <span>All {{activeSubcategory.name}}</span>
                                <span class="icon-chevron-lg-right"></span>
                            </router-link>

                        </li>
                        <template v-if="activeCategory && activeCategory.id === 'exclusive'">
                            <li
                                :data-e2e="`site-header.mobile-nav.performer-3`"
                            >
                                <router-link
                                    class="mobile-nav__nav-element mobile-nav__router"
                                    :to="fixPath(results[3].slug)"
                                >
                                    <span>{{results[3].name}}</span>
                                    <span class="icon-chevron-lg-right"></span>
                                </router-link>
                            </li>
                            <li
                                v-for="(category, index) in results.slice(0, 3)" :key="category.name"
                                :data-e2e="`site-header.mobile-nav.performer-${index}`"
                            >
                                <router-link
                                    class="mobile-nav__nav-element mobile-nav__router"
                                    :to="fixPath(category.slug)"
                                >
                                    <span>{{category.name}}</span>
                                    <span class="icon-chevron-lg-right"></span>
                                </router-link>
                            </li>
                        </template>
                        <template v-else>
                            <li
                                v-for="(category, index) in results" :key="category.name"
                                :data-e2e="`site-header.mobile-nav.performer-${index}`"
                            >
                                <router-link
                                    class="mobile-nav__nav-element mobile-nav__router"
                                    :to="$store.state.selectedLocation
                                        ? fixPath(`${category.slug}?lat=${$store.state.selectedLocation.latitude}&lon=${$store.state.selectedLocation.longitude}&location_id=${$store.state.selectedLocation.id}`)
                                        : fixPath(category.slug)"
                                >
                                    <span>{{category.name}}</span>
                                    <span class="icon-chevron-lg-right"></span>
                                </router-link>
                            </li>
                        </template>
                    </ul>
                </section>
            </transition>

            <transition name="slide-fade">
                <search-suggestions
                    :highlighted-suggestion="highlightedSuggestion"
                    :make-entity-route="makeEntityRoute"
                    :on-suggestion-click="onSuggestionClick"
                    :suggestions="suggestions" />
            </transition>
        </section>

        <section
            class="bulletin"
            v-if="isShowBulletin && bulletin && bulletin.message">
            <router-link
                class="bulletin__inner"
                v-if="bulletin.legal"
                @click.native="onClickBulletin"
                :to="{ query: updateQueryParams($route.query, { modal: 'bulletin-legal' }) }">
                <div
                    class="bulletin__content"
                    :class="{ 'bulletin__content--fluid': isFluid }">
                    <div class="bulletin__message">{{ bulletin.message }}</div>
                    <div class="bulletin__expiry">
                        <span v-if="bulletin.expiry">Expires: {{ formatDate(bulletin.expiry, 'MMMM D, YYYY') }}.</span>
                        <span>Click here for details.</span>
                    </div>
                </div>
            </router-link>
            <div
                class="bulletin__inner"
                v-else>
                <div
                    class="bulletin__content"
                    :class="{ 'bulletin__content--fluid': isFluid }">
                    <div class="bulletin__message">{{ bulletin.message }}</div>
                    <div
                        class="bulletin__expiry"
                        v-if="bulletin.expiry">
                        Expires: {{ formatDate(bulletin.expiry, 'MMMM D, YYYY') }}.
                    </div>
                </div>
            </div>
        </section>
    </header>
</template>

<script>
import {
    EVENT_NAME,
    HEADER
} from 'core/constants/googleAnalyticEvents'
import { CREDIT_CARD_TYPES } from 'core/types/CreditCard'
import { getPrimaryPerformer } from '../services/EventService'
import { makeEntityRoute } from '../services/EntityService'
import { mixin as onClickOutside } from 'vue-on-click-outside'
import config from 'config'
import DateUtil from 'core/utilities/DateUtil'
import inlineSearchSuggestions from './InlineSearchSuggestionsComponent.vue'
import resizable from '../mixins/Resizable'
import loyaltyFormattable from '../mixins/LoyaltyFormattable'
import searchSuggestions from './SearchSuggestionsComponent.vue'
import trackable from '../mixins/Trackable'
import Utils from 'core/utilities/Utils.js'

export default {
    components: {
        inlineSearchSuggestions,
        searchSuggestions
    },
    computed: {
        isExclusiveEvent () {
            return (this.event || {}).isExclusive
        },
        isShowSearchClose () {
            return this.query.length > 0 || this.isMobileNavOpen
        },
        showYear () {
            return DateUtil.getYearsDifference(DateUtil.parse(this.event.local_date), new Date()) > 0
        }
    },
    created () {
        this.trackableEventCategory = 'UX - Header'
        this.hasLoginButton = Boolean(config.auth && config.auth.hasLoginButton)
        this.collapseNavTimeout = null
        this.expandNavTimeout = null

        if (this.$googleTagManager) {
            this.$googleTagManager.pushEvent('dimension5', {
                hasRecentSearches: this.$store.state.recentSearches.length > 0
            })
        }

        if (this.$store.state.loyalty && this.$store.state.loyalty.credit_card_account) {
            this.lastFour = this.$store.state.loyalty.credit_card_account.lastFour
            const userCreditCard = this.$store.state.loyalty.credit_card_account.network
            this.creditCardType = CREDIT_CARD_TYPES[userCreditCard.toUpperCase()]
        }
    },
    data () {
        return {
            activeCategory: null,
            activeSubcategory: null,
            creditCardType: null,
            isAccountMenuOpen: false,
            isMobileNavOpen: false,
            isShowSuggestions: false,
            lastFour: null,
            results: [],
            subcategories: []
        }
    },
    methods: {
        updateQueryParams: Utils.updateQueryParams,
        addRecentSearch (search) {
            this.$store.commit('addRecentSearch', search)
        },
        clearCollapseNavTimeout () {
            if (this.collapseNavTimeout) {
                clearTimeout(this.collapseNavTimeout)
            }
        },
        clearExpandNavTimeout () {
            if (this.expandNavTimeout) {
                clearTimeout(this.expandNavTimeout)
            }
        },
        clearSearchQueryAndCloseMobileNav () {
            this.$emit('clear-search-query')
            this.isMobileNavOpen = false
        },
        closeAccountMenu () {
            this.isAccountMenuOpen = false
        },
        fixPath (slugOrRoute) {
            if (!slugOrRoute) { return '' }
            if (typeof slugOrRoute === 'object') { return slugOrRoute }
            if (slugOrRoute.startsWith('/')) { return slugOrRoute }
            return `/${slugOrRoute}`
        },
        formatDate: DateUtil.format,
        hideSearchSuggestions () {
            this.isShowSuggestions = false
        },
        logout () {
            this.closeAccountMenu()
            this.$store.dispatch('logout')
        },
        makeSearchItemKey (search) {
            if (search.route.name === 'search') { return search.route.query.q }
            return search.label
        },
        onCategoryButtonPressEnter (category) {
            if (this.activeCategory === category) {
                this.setActiveCategory(null)
            } else {
                this.setActiveCategory(category)
            }
        },
        onClickBackButton () {
            const lastEventsPath = this.$store.state.lastEventsPath
            const primaryPerformer = getPrimaryPerformer(this.event)

            if (lastEventsPath) {
                this.$router.push(lastEventsPath)
            } else if (primaryPerformer) {
                this.$router.push(makeEntityRoute('performer', primaryPerformer))
            } else {
                this.$router.push({ name: 'home' })
            }
        },
        onClickBulletin () {
            this.$googleTagManager.pushEvent(EVENT_NAME, HEADER.BULLETIN_SELECTED)
        },
        onClickLogin () {
            this.$googleTagManager.pushEvent(EVENT_NAME, HEADER.LOGIN_SELECTED)
        },
        onClickLogo () {
            this.$googleTagManager.pushEvent(EVENT_NAME, HEADER.PARTNER_LOGO_SELECTED)
        },
        onClickNavigationCategory () {
            this.$googleTagManager.pushEvent(EVENT_NAME, HEADER.NAVIGATION_TAXONOMY_SELECTED)
        },
        onClickOutsideSubnav () {
            this.setActiveCategory(null)
        },
        onOpenMobileNav () {
            this.isMobileNavOpen = !this.isMobileNavOpen && this.isWindowSmall && this.query.length === 0
        },
        onPrimaryNavMouseOver () {
            this.clearExpandNavTimeout()
            this.setCollapseNavTimeout()
        },
        onClickPerformer (performer) {
            this.$googleTagManager.pushEvent(EVENT_NAME,
                Object.assign({}, HEADER.NAVIGATION_PERFORMER_SELECTED, {
                    value: performer
                }))
        },
        onRecentSearchClick (search) {
            this.addRecentSearch(search)
            // TODO: deprecate this GA event in the future
            this.trackClickEvent('Recently Searched - Clicked')
            this.$googleTagManager.pushEvent(EVENT_NAME, HEADER.SEARCH_PILL_SELECTED)
        },
        onSearchInput () {
            this.onSearchInputChange(this.$refs.searchInput.value.trim())
        },
        removeRecentSearchAt (index) {
            // TODO: deprecate this GA event in the future
            this.$store.commit('removeRecentSearchAt', index)
            this.$googleTagManager.pushEvent(EVENT_NAME, HEADER.SEARCH_PILL_DISMISSED)
        },
        setActiveCategory (category) {
            this.clearCollapseNavTimeout()

            if (category === null) {
                this.activeCategory = null
                this.activeSubcategory = null
                this.results = []
                this.subcategories = []
            } else if (category.name === 'Sports') {
                this.activeCategory = category
                this.activeSubcategory = null
                this.results = []
                this.subcategories = category.children

                if (!this.isWindowSmall) {
                    this.setActiveSubcategory(category.children[0])
                }
            } else {
                this.activeCategory = category
                this.activeSubcategory = null
                this.results = category.children
                this.subcategories = []
            }
        },
        setActiveSubcategory (subcategory) {
            this.activeSubcategory = subcategory
            this.results = subcategory.children || []
        },
        setCollapseNavTimeout () {
            this.clearCollapseNavTimeout()
            this.collapseNavTimeout = setTimeout(() => this.setActiveCategory(null), 250)
        },
        setExpandNavTimeout (category) {
            this.clearExpandNavTimeout()
            this.expandNavTimeout = setTimeout(() => this.setActiveCategory(category), 250)
        },
        showSearchSuggestions () {
            this.isShowSuggestions = true
        },
        toggleAccountMenu () {
            this.isAccountMenuOpen = !this.isAccountMenuOpen
        }
    },
    mixins: [
        loyaltyFormattable,
        onClickOutside,
        resizable,
        trackable
    ],
    props: {
        bulletin: {
            default: null,
            required: false,
            type: Object
        },
        event: {
            default: null,
            required: false,
            type: Object
        },
        hasBigSearch: {
            default: false,
            required: false,
            type: Boolean
        },
        hasLogoTab: {
            default: false,
            required: false,
            type: Boolean
        },
        hasLogout: {
            default: false,
            required: false,
            type: Boolean
        },
        hasMyAccountButton: {
            default: false,
            required: false,
            type: Boolean
        },
        hasMyTicketsButton: {
            default: false,
            required: false,
            type: Boolean
        },
        highlights: {
            default () { return [] },
            required: false,
            type: Array
        },
        highlightedSuggestion: {
            default: null,
            required: false,
            type: Object
        },
        isFluid: {
            default: false,
            required: false,
            type: Boolean
        },
        isShowBulletin: {
            default: false,
            required: false,
            type: Boolean
        },
        isShowMenu: {
            default: false,
            required: false,
            type: Boolean
        },
        isShowPageNotFound: {
            default: false,
            required: false,
            type: Boolean
        },
        isShowPartnerBanner: {
            default: false,
            required: false,
            type: Boolean
        },
        isShowSearch: {
            default: false,
            required: false,
            type: Boolean
        },
        isTall: {
            default: false,
            required: false,
            type: Boolean
        },
        logoDesktop: {
            default () { return null },
            required: false,
            type: Object
        },
        logoMobile: {
            default () { return null },
            required: false,
            type: Object
        },
        makeEntityRoute: {
            default (entityType, entity) {
                return ''
            },
            required: false,
            type: Function
        },
        onDecrementHighlightedSuggestionIndex: {
            default () {},
            required: false,
            type: Function
        },
        onIncrementHighlightedSuggestionIndex: {
            default () {},
            required: false,
            type: Function
        },
        onSearch: {
            default () {},
            required: false,
            type: Function
        },
        onSearchInputChange: {
            default () {},
            required: false,
            type: Function
        },
        onSetActiveCategory: {
            default () {},
            required: false,
            type: Function
        },
        onSuggestionClick: {
            default () {},
            required: false,
            type: Function
        },
        partnerLogoDesktop: {
            default () { return null },
            required: false,
            type: Object
        },
        partnerLogoMobile: {
            default () { return null },
            required: false,
            type: Object
        },
        query: {
            default: '',
            required: false,
            type: String
        },
        suggestions: {
            default () { return {} },
            required: false,
            type: Object
        },
        userProfile: {
            default: null,
            required: false,
            type: Object
        }
    },
    watch: {
        $route: {
            handler (route) {
                this.setActiveCategory(null)
                this.isMobileNavOpen = false
            }
        },
        activeCategory () {
            this.onSetActiveCategory(this.activeCategory)
        },
        query: {
            immediate: true,
            handler () {
                this.$nextTick(() => {
                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.value = this.query

                        if (this.query.length > 0) {
                            this.isMobileNavOpen = false
                        }
                    }
                })
            }
        }
    }
}
</script>

<style lang="postcss" scoped>
@import 'theme';

header {
    background-image: linear-gradient(90deg, var(--c-header-gradient-left) 0%, var(--c-header-gradient-right) 100%);
    width: 100%;
    -webkit-font-smoothing: antialiased;

    &.header--tall {
        background-image: linear-gradient(90deg, var(--c-header-gradient-left) 0%, var(--c-header-gradient-right) 100%);
        padding-bottom: 70px;
    }

    @media (--mq-large-min) {
        &.header--tall {
            padding-bottom: 90px;
        }
    }

    @media (--mq-full-min) {
        &.header--tall {
            padding-bottom: 125px;
        }
    }
}

.account {
    & .account__menu {
        background-image: linear-gradient(90deg, var(--c-header-gradient-left) 0%, var(--c-header-gradient-right) 100%);
        box-shadow: 0 2px 8px 0 rgba(68, 70, 73, 0.75);
        left: 0;
        list-style-type: none;
        margin: 0;
        overflow: hidden;
        position: absolute;
        right: 0;
        top: 0;
        width: 100%;
        z-index: 1000;
    }

    & .account__menu-action {
        background-color: rgba(255, 255, 255, .15);
        border: none;
        color: #fff;
        cursor: pointer;
        display: block;
        font-size: var(--fs-large);
        padding: 14px 20px;
        text-align: left;
        text-decoration: none;
        width: 100%;

        @media (--mq-large-min) {
            padding: 8px;
            text-align: center;
        }

        &:active,
        &:hover,
        &:focus {
            background: linear-gradient(90deg, rgba(255, 255, 255, .2) 0%, rgba(255, 255, 255, 0) 100%);
        }

        & .icon-chevron-lg-right {
            float: right;
            margin-right: -8px;
        }
    }

    & .account__menu-close {
        background: transparent;
        border: none;
        cursor: pointer;
        height: 30px;
        padding: 4px 0 0;
        position: absolute;
        right: 5px;
        top: 5px;
        width: 30px;
    }

    & .account__menu-close-icon {
        color: #fff;
        font-size: 16px;
        position: absolute;
        right: 5px;
        top: 36px;
    }

    & .account__menu-item {
        border-top: 1px solid #D8D8D8;
        color: white;
        text-align: left;
        width: 100%;
        @media (--mq-large-min) {
            text-align: center;
        }

        &:first-child {
            background-color: var(--c-header-gradient-right);
            border-top: none;
            display: flex;
            flex-direction: column;
            padding: 24px 20px;
        }

        &:last-child {
            border-bottom: 1px solid #D8D8D8;
        }

        & .account__menu-item--credit-cards__icon {
            height: auto;
            margin-right: 4px;
            padding-top: 0px;
            width: 22px;
        }

        & .account__menu-item--welcome {
            font-size: var(--fs-large);
            font-weight: var(--fw-bold);
            margin-bottom: 4px;
        }

        & .account_menu-item--user-info {
            display: flex;
            align-items: center;
            & .user-info__credit-card {
                align-items: center;
                display: flex;
                & .user-info__middot {
                    font-weight: var(--fw-bold);
                    margin-right: 4px;
                }

                & .user-info__pipe {
                    font-weight: 200;
                    margin: 0 8px;
                }
            }

            & .user-info__last-four, & .user-info__loyalty-balance {
                font-size: var(--fs-small);
                font-weight: 200;
            }
        }
    }

    & .account__welcome {
        color: #fff;
        font-size: var(--fs-large);
        margin-left: 10px;
        padding: 0;

        &.account__welcome--button {
            background: none;
            border: none;
            cursor: pointer;
            height: max-content;
            outline: none;
            width: 30px;
            &:disabled {
                cursor: default;
                opacity: 1;
            }
            &.has-credit-card {
                padding-right: 100px;
                @media (--mq-large-min) {
                    padding-right: 0;
                }
            }
        }
    }

    & .account__welcome-icon {
        font-size: var(--fs-small);
        margin-right: 16px;
        margin-top: 12px;
        position: absolute;
        right: 0;
        top: 50%;
        @media (--mq-large-min) {
            font-size: var(--fs-normal);
            margin-right: 0;
            margin-top: -8px;
        }
        &.no-credit-card--mobile {
            top: 8px;
        }
    }

    & .account__welcome-inner {
        display: block;
        line-height: 1.2em;
        white-space: nowrap;

        &:focus,
        &:active {
            outline: none;
        }

        &.account__welcome-inner--expandable {
            padding-right: 16px;
        }

        & .icon-profile {
            position: relative;
            /*top: 8px;*/
            /*right: 100px;*/
        }
    }

    @media (--mq-large-min) {
        position: relative;

        & .account__menu {
            background-color: #fff;
            background-image: none;
            border-radius: var(--d-border-radius);
            border-top-right-radius: 0;
            left: auto;
            top: 100%;
            width: 160px;
        }

        & .account__menu-action {
            color: var(--c-primary);
            text-align: left;

            &:hover {
                border-left: 4px solid var(--c-primary);
            }
        }

        & .account__menu-item {
            border-top: 1px solid #D8D8D8;

            &:first-child {
                border-top: none;
            }
        }

        & .account__welcome {
            margin-left: 20px;

            &.account__welcome--button {
                height: auto;
                width: auto;
            }
        }
    }
}

.bulletin {
    background-color: white;
    border-bottom: 1px solid white;
    border-top: 1px solid white;
    width: 100%;

    & .bulletin__content {
        color: white;
        margin: 0 auto;
        max-width: 100%;
        padding: var(--site-padding);
        width: var(--site-container-width);

        &.bulletin__content--fluid {
            width: 100%;
        }
    }

    & .bulletin__expiry {
        display: block;
        font-size: 12px;
        margin-top: 10px;
    }

    & .bulletin__inner {
        background: var(--c-bulletin-bg);
        display: block;
        text-align: center;
        text-decoration: none;
    }

    & .bulletin__message {
        display: block;
        font-size: 12px;
        font-weight: var(--fw-medium);
        color: var(--c-bulletin-text-color)
    }

    @media (--mq-medium-min) {
        & .bulletin__message {
            font-size: var(--fs-large);
        }
    }
}

.inline-search {
    flex: 1 1 100%;
    margin-left: 10px;
    position: relative;

    & .icon-magnify {
        left: 7px;
        position: absolute;
        top: 7px;
    }

    & .inline-search__input {
        background: transparent;
        border: 1px solid #fff;
        border-radius: 20px;
        color: #fff;
        font-size: 14px;
        height: 30px;
        margin: 0;
        padding: 4px 4px 4px 26px;
        width: 100%;
        -webkit-appearance: textfield;
        &::placeholder {
            color: #fff;
        }
    }

    & .inline-search__suggestion-primary {
        font-weight: normal;
        margin: 0;
        margin: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    & .inline-search__suggestion-secondary {
        font-size: 12px;
        font-weight: 300;
        margin: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    @media (--mq-large-min) {
        flex: 0 1 260px;

        & .icon-magnify {
            left: 8px;
            top: 8px;
        }

        & .inline-search__input {
            padding: 5px 5px 5px 28px;
        }
    }
}

.nav-bar {
    color: #f5f5f5;
    height: 60px;

    & .nav-bar__back {
        background: transparent;
        border: none;
        color: #fff;
        cursor: pointer;
        font-size: 24px;
        font-weight: 500;
        height: 30px;
        margin-right: 12px;
        position: relative;
        text-decoration: none;
        width: 25px;
    }

    & .nav-bar__back-icon {
        left: -5px;
        position: absolute;
        top: 2px;
    }

    & .nav-bar__inner {
        align-items: center;
        display: flex;
        flex-direction: row;
        height: 100%;
        justify-content: center;
        margin: 0 auto;
        max-width: 100%;
        padding: 10px 16px;
        width: var(--site-container-width);

        &.nav-bar__inner--fluid {
            width: 100%;
        }

        & .event-information {
            flex: 1 1 100%;
            min-width: 1px;
            padding: 12px 0;
            -webkit-font-smoothing: antialiased;

            @media (--mq-medium-max) {
                text-align: center;
            }

            & .event-title-container {
                display: flex;
                & .event-title {
                    padding: 0px;
                    margin: 0px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    width: 100%;
                    font-size: var(--fs-largest);
                    font-weight: var(--fw-medium);
                    color: white;
                }
            }

            & .event-details {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                font-size: var(--fs-normal);
                font-weight: var(--fw-light);
                color: white;
            }
        }
    }

    & .nav-bar__logo {
        display: block;
        height: auto;

        & img {
            display: block;
            height: 40px;
            max-height: 100%;
        }
    }

    & .nav-bar__logo-container {
        margin-right: 20px;

        &.nav-bar__logo-container--tab {
            align-items: center;
            background-color: white;
            border-radius: 0 0 5px 5px;
            display: flex;
            height: 50px;
            margin-top: -10px;
            padding: 0 30px;
        }
    }

    & .nav-bar__right {
        align-items: center;
        display: flex;
        flex: 1 1 100%;
        justify-content: flex-end;
        @media (--mq-large-min) {
            margin-left: auto;
            margin-right: 0;
        }
    }

    @media (--mq-large-min) {
        & .nav-bar__inner {
            padding: 0 16px;
        }

        & .nav-bar__right {
            &.nav-bar__right--fluid {
                flex: 1 1 100%;
            }
        }
    }
}

.not-found {
    display: none;

    @media (--mq-large-min) {
        color: #fff;
        display: block;
        margin-bottom: 40px;
        text-align: center;

        & h1 {
            font-size: 48px;
            font-weight: 500;
            margin: 40px 0 10px;
        }

        & h2 {
            font-size: 20px;
            font-weight: normal;
            line-height: 1.4em;
            margin: 0;
        }
    }
}

.partner-banner {
    background-color: var(--c-primary);
    color: #FFFFFF;
    display: flex;
    font-size: var(--fs-normal);
    padding-left: 5px;
    @media (--mq-medium-min) {
        font-size: var(--fs-large);
    }
    & .partner-banner__copy {
        padding: 15px 15px 20px 0;
        & .partner-banner__legal {
            color: #FFFFFF;
            text-decoration: underline;
        }
    }
}

.primary-nav {
    align-self: stretch;

    & .primary-nav__item {
        z-index: 5;

        & button {
            border: none;
            padding: 15px 10px;

            &:focus,
            &:hover {
                border-bottom: 5px solid rgba(255, 255, 255, 0.5);
                outline: none;
                padding: 15px 10px 10px;
            }
        }

        &.primary-nav__item--active {
            & button {
                border-bottom: 5px solid #fff;
                padding: 15px 10px 10px;
            }
        }
    }

    & .primary-nav__item-button {
        background: transparent;
        color: #f5f5f5;
        cursor: pointer;
        font-size: var(--fs-large);
        height: 100%;
        white-space: nowrap;
    }

    & .primary-nav__list {
        align-items: stretch;
        display: flex;
        flex-direction: row;
        height: 100%;
        list-style: none;
        margin: 0;
        padding: 0;
    }
}

.recents {
    align-items: center;
    color: #fff;
    display: flex;
    flex-direction: row;
    margin-bottom: 15px;
    margin-left: 15px;
    max-width: 100%;
    width: 660px;

    @media (--mq-medium-min) {
        margin: 10px auto;
        padding-left: 15px;
    }

    & .recents__list {
        display: flex;
        flex: 1 1 100%;
        list-style: none;
        margin-right: 30px;
        min-width: 0;
        padding: 0;
        overflow: auto;

        @media (--mq-medium-min) {
            margin: 0;
            overflow: initial;
        }
    }

    & .recents__search {
        align-items: center;
        background: rgba(255,255,255,0.2);
        border-radius: 5px;
        display: flex;
        flex: 0 1 auto;
        flex-direction: row;
        margin: 5px;
        overflow: initial;

        & .icon-cancel {
            background: transparent;
            border: none;
            color: #f5f5f5;
            cursor: pointer;
            font-size: var(--fs-small);
            height: 100%;
            padding: 0 5px;
            transition: 0.5s;
        }

        & .link {
            color: #f5f5f5;
            display: block;
            flex: 1 1 auto;
            font-weight: 300;
            overflow: hidden;
            padding: 5px;
            text-decoration: none;
            text-overflow: ellipsis;
            transition: 0.5s;
            white-space: nowrap;
        }

        & .icon-cancel,
        & .link {
            &:focus,
            &:hover {
                background: rgba(255, 255, 255, 0.25);
                outline: none;
                transition: 0.5s;
            }
        }

        @media (--mq-medium-min) {
            overflow: hidden;
        }
    }

    & span {
        margin-right: 5px;
    }
}

.search {
    padding-bottom: 0;

    & .icon-close {
        font-size: 16px;
        margin-left: 14px;
        margin-top: 5px;
    }

    & .icon-magnify {
        color: #fff;
        font-size: 20px;
        left: 0;
        position: absolute;
        top: 4px;
    }

    & .search__form {
        color: #fff;
        margin: 0;
        max-width: 100%;
        padding: var(--site-padding);
        width: 660px;
    }

    & .search__form-inner {
        border-bottom: 2px solid #fff;
        display: flex;
        flex-direction: row;
        position: relative;
        width: 100%;
    }

    & .search__input {
        background: transparent;
        border: none;
        color: #fff;
        font-size: 16px;
        margin-left: 22px;
        outline: none;
        padding: 5px;
        width: 100%;
        -webkit-appearance: textfield;

        &::placeholder {
            color: inherit;
        }
    }

    @media (--mq-medium-min) {
        padding-bottom: 40px;

        & .icon-magnify {
            font-size: 24px;
            top: 15px;
        }

        & .search__form {
            margin: 10px auto;
        }

        & .search__input {
            font-size: 20px;
            margin-left: 30px;
            padding: var(--site-padding);
        }
    }
}

.sign-in-button {
    background: none;
    color: #fff;
    cursor: pointer;
    font-size: var(--fs-large);
    height: 40px;
    padding: 8px 8px 8px 24px;
    position: relative;
    text-decoration: none;
    white-space: nowrap;
    width: 40px;

    & .icon-signin {
        left: 11px;
        position: absolute;
        top: 11px;
    }

    @media (--mq-large-min) {
        height: auto;
        margin-left: 20px;
        width: auto;

        & .icon-signin {
            left: 4px;
            top: 9px;
        }
    }
}

.subnav {
    background-image: linear-gradient(90deg, var(--c-header-gradient-left) 0%, var(--c-header-gradient-right) 100%);
    border-bottom: 1px solid #fff;
    border-top: 1px solid #fff;
    box-shadow: 0 1px 8px rgba(0,0,0,.5);
    position: absolute;
    width: 100%;
    z-index: 100;

    & .subnav__inner {
        display: flex;
        flex-direction: row;
        margin: 0 auto;
        width: 700px;
    }

    & .subnav__nav-left {
        border-right: 1px solid #fff;
        flex: 0 0 140px;
        list-style: none;
        margin: 0;
        padding: 10px 0;
    }

    & .subnav__nav-left-item {
        width: 100%;

        &.subnav__nav-left-item--active {
            background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 100%);
        }
    }

    & .subnav__nav-left-text {
        color: #f5f5f5;
        display: block;
        font-weight: var(--fw-light);
        padding: 5px;
        text-decoration: none;
    }

    & .subnav__nav-right {
        columns: 4 auto;
        flex: 1 1 100%;
        list-style: none;
        margin: 0;
        padding: 10px;
        width: 100%;

        &.subnav__nav-right--exclusive {
            display: flex;
            justify-content: space-between;
        }
    }

    & .subnav__nav-right-item {
        break-inside: avoid;
    }

    & .subnav__nav-right-link {
        color: #f5f5f5;
        display: block;
        font-weight: var(--fw-light);
        margin-left: 4px;
        padding: 5px 10px;
        text-decoration: none;

        &.subnav__nav-see-all {
            border: 1px solid #FFFFFF;
            border-radius: 2px;
            padding: 5px 15px;
            margin-left: 0;
            text-align: center;

            &:focus,
            &:hover {
                background: rgba(255, 255, 255, 0.2);
                border-left: 1px solid #FFFFFF;
            }
        }

        &:focus,
        &:hover {
            background: linear-gradient(90deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%);
            border-left: 4px solid #fff;
            margin-left: 0;
        }
    }
}

.mobile-nav {
    & .mobile-nav__nav-element {
        border-top: 1px solid white;
        color: white;
        display: flex;
        font-size: var(--fs-large);
        height: 60px;
        justify-content: space-between;
        line-height: 20px;
        padding: 20px 14px;
    }

    & .mobile-nav__nav-header {
        align-items: center;
        font-weight: 400;
        justify-content: left;
        margin: 0;
    }

    & .mobile-nav__nav-header--uncollapsed {
        background-color: rgba(255, 255, 255, 0.15)
    }

    & .mobile-nav__icon {
        margin: 4px 8px 0 0;
    }

    & .mobile-nav__router {
        text-decoration: none;
        width: 100%;
    }

    & .mobile-nav__performers,
    & .mobile-nav__subcategories {
        background-color: rgba(255, 255, 255, 0.15)
    }
}

/* Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
    opacity: 1;
    max-height: 1200px;
    transform: translateY(0);
    transition: all 1s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
    opacity: 0;
    max-height: 0;
    transform: translateY(-20px);
}

</style>
